import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Sparkles, Check, Printer } from "lucide-react";
import { toast } from "sonner";
import { useApi, useFetch, useMe, errMsg, fmtDate } from "../lib/api";
import { Btn, Card, Empty, PageHeader, Sel, StateBadge } from "../components/common";

const LIST_KEYS = ["scope", "deliverables", "execution_approach", "assumptions", "dependencies", "exclusions"];
const TEXT_KEYS = ["client_requirement", "technical_assessment", "commercial_model"];
const label = (k) => k.replaceAll("_", " ").replace(/^\w/, (c) => c.toUpperCase());
const toText = (v) => (Array.isArray(v) ? v.map((x) => (typeof x === "object" ? Object.values(x).join(" — ") : x)).join("\n") : v || "");

function Editor({ p, onSaved }) {
  const api = useApi();
  const { isAdmin } = useMe();
  const [c, setC] = useState({});
  useEffect(() => {
    const o = {};
    [...TEXT_KEYS, ...LIST_KEYS, "timeline", "payment_milestones"].forEach((k) => (o[k] = toText(p.content[k])));
    o.title = p.content.title || p.title;
    setC(o);
  }, [p]);
  const save = async (status) => {
    const content = { ...c };
    [...LIST_KEYS, "timeline", "payment_milestones"].forEach((k) => (content[k] = (c[k] || "").split("\n").filter(Boolean)));
    try { await api.put(`/proposals/${p.id}`, status ? { status } : { content }); toast.success(status ? `Marked ${status}` : "Saved (back to draft)"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testid="proposal-editor" title={<span className="flex items-center gap-2">{c.title}<StateBadge s={p.status} testid="proposal-status" /></span>}
      action={<div className="flex gap-2 print:hidden">
        {isAdmin && <Btn variant="ghost" onClick={() => save()} data-testid="proposal-save">Save edits</Btn>}
        {isAdmin && p.status === "draft" && <Btn onClick={() => save("approved")} data-testid="proposal-approve"><Check size={14} /> Approve</Btn>}
        {isAdmin && p.status === "approved" && <Btn variant="dark" onClick={() => save("sent")} data-testid="proposal-mark-sent">Mark sent</Btn>}
        {isAdmin && p.status === "sent" && <><Btn variant="ghost" onClick={() => save("accepted")} data-testid="proposal-accepted">Accepted</Btn><Btn variant="ghost" onClick={() => save("rejected")} data-testid="proposal-rejected">Rejected</Btn></>}
        <Btn variant="ghost" onClick={() => window.print()} data-testid="proposal-print"><Printer size={14} /> Print / PDF</Btn></div>}>
      <p className="mb-4 text-xs text-slate-500">{p.company} · {p.engagement.replace("_", " ")} · {p.price || "price TBC"} {p.currency} · <Link className="text-blue-700" to={`/admin/opportunities/${p.opportunity_id}`}>lead</Link>. Quotations are never sent automatically.</p>
      {[...TEXT_KEYS.slice(0, 2), ...LIST_KEYS.slice(0, 3), "timeline", "assumptions", "dependencies", "commercial_model", "payment_milestones", "exclusions"].map((k) => (
        <div key={k} className="mb-4"><div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label(k)}</div>
          <textarea data-testid={`proposal-${k}`} disabled={!isAdmin} className="dm-input min-h-[70px] w-full py-2 text-sm" value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} rows={Math.min(10, (c[k] || "").split("\n").length + 1)} /></div>))}
    </Card>
  );
}

export default function Proposals() {
  const api = useApi();
  const { isAdmin } = useMe();
  const [sp] = useSearchParams();
  const { data, reload } = useFetch("/proposals");
  const { data: opps } = useFetch("/opportunities?sort=score");
  const [sel, setSel] = useState(null);
  const [g, setG] = useState({ opportunity_id: sp.get("opportunity") || "", commercial_model: "fixed_price", rate_or_price: "", currency: "USD", duration_hint: "" });
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try { const p = await api.post("/proposals/generate", g); toast.success("Proposal draft created"); await reload(); setSel(p.id); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const cur = data?.find((p) => p.id === sel);
  return (
    <div>
      <PageHeader title="Proposals" subtitle="AI-assisted drafts with requirement, assessment, scope, deliverables, approach, timeline, assumptions, dependencies, commercial model, milestones and exclusions." />
      {isAdmin && <Card title="Generate proposal" testid="proposal-generator" className="mb-6">
        <div className="grid gap-2 md:grid-cols-6">
          <div className="md:col-span-2"><Sel testid="prop-opportunity" value={g.opportunity_id} onChange={(v) => setG({ ...g, opportunity_id: v })} placeholder="Select opportunity…" options={(opps || []).slice(0, 300).map((o) => ({ value: o.id, label: `${o.score} · ${o.company} — ${o.title.slice(0, 60)}` }))} /></div>
          <Sel testid="prop-model" value={g.commercial_model} onChange={(v) => setG({ ...g, commercial_model: v })} options={[{ value: "fixed_price", label: "Fixed price" }, { value: "hourly", label: "Hourly" }, { value: "daily", label: "Daily rate" }, { value: "retainer", label: "Monthly retainer" }]} />
          <input data-testid="prop-price" className="dm-input" placeholder="Price / rate (optional)" value={g.rate_or_price} onChange={(e) => setG({ ...g, rate_or_price: e.target.value })} />
          <Sel testid="prop-currency" value={g.currency} onChange={(v) => setG({ ...g, currency: v })} options={["USD", "GBP", "EUR", "SGD", "AUD", "AED", "INR"]} />
          <Btn data-testid="prop-generate" onClick={gen} disabled={!g.opportunity_id || busy}><Sparkles size={14} /> {busy ? "Generating…" : "Generate"}</Btn>
        </div>
      </Card>}
      <div className="grid gap-6 lg:grid-cols-4">
        <Card className="lg:col-span-1" testid="proposal-list">
          {!data?.length ? <Empty text="No proposals yet." testid="proposals-empty" /> : data.map((p) => (
            <button key={p.id} data-testid={`proposal-item-${p.id}`} onClick={() => setSel(p.id)} className={`block w-full border-b border-slate-100 px-2 py-2 text-left text-sm transition-colors hover:bg-slate-50 ${sel === p.id ? "bg-blue-50" : ""}`}>
              <div className="font-medium">{p.company}</div><div className="flex items-center justify-between text-xs text-slate-500">{fmtDate(p.created_at)}<StateBadge s={p.status} /></div>
            </button>))}
        </Card>
        <div className="lg:col-span-3">{cur ? <Editor p={cur} onSaved={reload} /> : <Card><Empty text="Select or generate a proposal." /></Card>}</div>
      </div>
    </div>
  );
}
